import { h } from "../lib/dom";
import {
  CATEGORIES,
  CITY_PRESETS,
  CUISINE_PRESETS,
  type CategoryKey,
  NEIGHBORHOOD_PRESETS,
  NEGATIVE_REVIEW_MIN_VISITS,
  NEGATIVE_REVIEW_THRESHOLD,
  PRE_PUBLISH_CHECKLIST,
  READER_DISCLOSURE_TEXT,
  type ReturnVerdict,
  SCORE_WEIGHTS_VERSION,
} from "../lib/constants";
import { fileExtension } from "../lib/format";
import { linkContributionsToReview } from "../lib/ledger";
import { clampScore, scoreBand, weightedFinal, type ScoreMap } from "../lib/scoring";
import { photoFilename, reviewSlug } from "../lib/slug";
import {
  REVIEW_IMAGES_BUCKET,
  supabase,
  type ParagraphColumn,
  type ReviewAssetRow,
  type ReviewRequestRow,
  type ReviewRow,
  type ScoreColumn,
} from "../lib/supabase";

export interface EditorOptions {
  reviewId: string | null;
  requestId: string | null;
  onSaved: (reviewId: string) => void;
  onClose: () => void;
}

type DraftState = {
  id: string | null;
  restaurant_name: string;
  address: string;
  city: string;
  neighborhood: string;
  cuisine: string;
  price_range: "" | "$" | "$$" | "$$$";
  date_visited: string;
  what_ordered: string;
  visit_count: number;
  independent_paid_visit_completed: boolean;
  scores: ScoreMap;
  paragraphs: Record<ParagraphColumn, string>;
  verdict: string;
  return_verdict: ReturnVerdict | "";
  return_condition: string;
  reader_requested: boolean;
  contribution_received: boolean;
  comped: boolean;
  published: boolean;
  reviewRequestId: string | null;
  assets: ReviewAssetRow[];
  checklist: Record<string, boolean>;
};

const PARAGRAPH_COLUMNS: Record<CategoryKey, ParagraphColumn | null> = {
  flavor: "paragraph_flavor",
  seasoning: "paragraph_seasoning",
  texture: "paragraph_texture",
  presentation: "paragraph_presentation",
  value: "paragraph_value",
  service: "paragraph_service",
  would_return: null,
};

function emptyDraft(): DraftState {
  return {
    id: null,
    restaurant_name: "",
    address: "",
    city: "",
    neighborhood: "",
    cuisine: "",
    price_range: "",
    date_visited: "",
    what_ordered: "",
    visit_count: 1,
    independent_paid_visit_completed: true,
    scores: {
      flavor: null,
      seasoning: null,
      texture: null,
      presentation: null,
      value: null,
      service: null,
      would_return: null,
    },
    paragraphs: {
      paragraph_flavor: "",
      paragraph_seasoning: "",
      paragraph_texture: "",
      paragraph_presentation: "",
      paragraph_value: "",
      paragraph_service: "",
    },
    verdict: "",
    return_verdict: "",
    return_condition: "",
    reader_requested: false,
    contribution_received: false,
    comped: false,
    published: false,
    reviewRequestId: null,
    assets: [],
    checklist: {},
  };
}

export function renderEditor(opts: EditorOptions): HTMLElement {
  const state: DraftState = emptyDraft();
  const root = h("main", { class: "page editor" });
  const status = h("p", { class: "editor-status", "aria-live": "polite" });

  let finalScoreValueEl: HTMLElement | null = null;
  let finalScoreBandEl: HTMLElement | null = null;
  let guardrailsSlotEl: HTMLElement | null = null;
  let disclosureSlotEl: HTMLElement | null = null;
  let requestLinkSlotEl: HTMLElement | null = null;
  let publishBtnEl: HTMLButtonElement | null = null;

  function setStatus(text: string, kind: "info" | "error" = "info"): void {
    status.textContent = text;
    status.className = `editor-status editor-status-${kind}`;
  }

  function updateDerivedUI(): void {
    const final = weightedFinal(state.scores);
    const finalText = final === null ? "—" : final.toFixed(1);
    const guardrails = checkGuardrails(state, final);

    if (finalScoreValueEl) finalScoreValueEl.textContent = `${finalText} / 10`;
    if (finalScoreBandEl) finalScoreBandEl.textContent = scoreBand(final);
    if (guardrailsSlotEl) {
      guardrailsSlotEl.replaceChildren(renderGuardrailsContent(guardrails));
    }
    if (publishBtnEl) publishBtnEl.disabled = guardrails.blockers.length > 0;
    if (disclosureSlotEl) {
      disclosureSlotEl.replaceChildren(
        ...(state.contribution_received || state.comped
          ? [h("blockquote", { class: "disclosure" }, h("p", {}, READER_DISCLOSURE_TEXT))]
          : []),
      );
    }
    if (requestLinkSlotEl) {
      requestLinkSlotEl.replaceChildren(
        ...(state.reviewRequestId
          ? [
              h(
                "p",
                { class: "section-hint section-hint-ok" },
                "Linked to submission ",
                h("code", {}, state.reviewRequestId),
                ". Publishing will attach ledger entries and mark the submission reviewed.",
              ),
            ]
          : []),
      );
    }
  }

  function paint(): void {
    const scrollY = window.scrollY;
    const final = weightedFinal(state.scores);
    const finalText = final === null ? "—" : final.toFixed(1);
    const guardrails = checkGuardrails(state, final);

    finalScoreValueEl = h("p", { class: "final-score-value" }, `${finalText} / 10`);
    finalScoreBandEl = h("p", { class: "final-score-band" }, scoreBand(final));
    guardrailsSlotEl = h("div", { class: "guardrails-slot" });
    guardrailsSlotEl.replaceChildren(renderGuardrailsContent(guardrails));
    disclosureSlotEl = h("div", { class: "disclosure-slot" });
    publishBtnEl = h(
      "button",
      {
        class: "btn btn-primary",
        type: "button",
        disabled: guardrails.blockers.length > 0,
        onclick: () => publish(state, true, paint, setStatus, opts),
      },
      state.published ? "Re-save published" : "Publish",
    ) as HTMLButtonElement;

    root.replaceChildren(
      h(
        "header",
        { class: "page-head" },
        h(
          "div",
          {},
          h("p", { class: "eyebrow" }, "Review editor"),
          h(
            "h1",
            { class: "page-title" },
            state.restaurant_name || "New review",
          ),
        ),
        h(
          "div",
          { class: "editor-actions" },
          h(
            "button",
            {
              class: "btn btn-ghost",
              type: "button",
              onclick: () => opts.onClose(),
            },
            "Back",
          ),
          h(
            "button",
            {
              class: "btn btn-primary",
              type: "button",
              onclick: () => saveDraft(state, paint, setStatus, opts),
            },
            state.id ? "Save changes" : "Save draft",
          ),
        ),
      ),
      status,
      h("p", { class: "form-required-note" }, "Fields marked ", h("span", { class: "required-mark" }, "*"), " are required to publish."),

      section(
        "Restaurant",
        h(
          "div",
          { class: "form-grid" },
          field(
            "Restaurant name",
            textInput(state.restaurant_name, (v) => (state.restaurant_name = v)),
            undefined,
            true,
          ),
          field(
            "Cuisine",
            comboInput(state.cuisine, CUISINE_PRESETS, (v) => (state.cuisine = v), "Italian"),
            "Pick from the list or type your own.",
          ),
          field(
            "Full address",
            textInput(state.address, (v) => (state.address = v), "1234 Hillsborough St"),
            undefined,
            true,
          ),
          field(
            "City",
            comboInput(state.city, CITY_PRESETS, (v) => (state.city = v), "Raleigh"),
            "Pick from the list or type your own.",
            true,
          ),
          field(
            "Neighborhood",
            comboInput(state.neighborhood, NEIGHBORHOOD_PRESETS, (v) => (state.neighborhood = v), "Downtown"),
            "Pick from the list or type your own.",
          ),
          field(
            "Price range",
            priceRangeSelect(state.price_range, (v) => {
              state.price_range = v;
              updateDerivedUI();
            }),
            undefined,
            true,
          ),
        ),
      ),

      section(
        "Visit",
        h(
          "div",
          { class: "form-grid" },
          field(
            "Date visited",
            dateInput(state.date_visited, (v) => {
              state.date_visited = v;
              updateDerivedUI();
            }),
            undefined,
            true,
          ),
          field(
            "Total visits to this restaurant",
            numberInput(state.visit_count, 1, 99, 1, (v) => {
              state.visit_count = Math.max(1, Math.round(v));
              updateDerivedUI();
            }),
          ),
          field(
            "Independent paid visit completed",
            checkbox(state.independent_paid_visit_completed, (v) => {
              state.independent_paid_visit_completed = v;
              updateDerivedUI();
            }),
            "Required if the visit was funded or comped. Cannot publish from a funded/comped visit alone.",
          ),
        ),
        field(
          "Everything ordered (one item per line)",
          textarea(state.what_ordered, (v) => {
            state.what_ordered = v;
            updateDerivedUI();
          }, 3),
          undefined,
          true,
        ),
      ),

      section(
        "Scores",
        h(
          "p",
          { class: "section-hint" },
          "Each category 1–10 (",
          h("span", { class: "required-mark" }, "*"),
          " required). Weighted final updates live (",
          h("code", {}, SCORE_WEIGHTS_VERSION),
          ").",
        ),
        h(
          "div",
          { class: "scores-grid" },
          ...CATEGORIES.map((cat) =>
            scoreRow(state, cat.key, updateDerivedUI),
          ),
        ),
        h(
          "div",
          { class: "final-score" },
          h("p", { class: "final-score-label" }, "Weighted final"),
          finalScoreValueEl,
          finalScoreBandEl,
        ),
      ),

      section(
        "Paragraphs",
        h(
          "p",
          { class: "section-hint" },
          "One honest paragraph per scoring category. No filler. Six paragraphs total — the seventh category lives in the verdict.",
        ),
        ...CATEGORIES.filter((c) => PARAGRAPH_COLUMNS[c.key] !== null).map((cat) => {
          const col = PARAGRAPH_COLUMNS[cat.key] as ParagraphColumn;
          return field(
            cat.label,
            textarea(
              state.paragraphs[col],
              (v) => {
                state.paragraphs[col] = v;
                updateDerivedUI();
              },
              4,
              `Ask yourself: ${cat.ask}`,
            ),
            undefined,
            true,
          );
        }),
      ),

      section(
        "Verdict",
        field(
          "Verdict (one sentence)",
          textInput(state.verdict, (v) => {
            state.verdict = v;
            updateDerivedUI();
          }, "One honest sentence."),
          undefined,
          true,
        ),
        h(
          "div",
          { class: "form-grid" },
          field(
            "Would I return?",
            returnSelect(state.return_verdict, (v) => {
              state.return_verdict = v;
              updateDerivedUI();
            }),
            undefined,
            true,
          ),
          field(
            "Optional condition (if Maybe)",
            textInput(state.return_condition, (v) => (state.return_condition = v)),
          ),
        ),
      ),

      section(
        "Disclosures",
        h(
          "div",
          { class: "form-grid" },
          field(
            "Reader-requested",
            checkbox(state.reader_requested, (v) => {
              state.reader_requested = v;
              updateDerivedUI();
            }),
          ),
          field(
            "Reader contribution received",
            checkbox(state.contribution_received, (v) => {
              state.contribution_received = v;
              updateDerivedUI();
            }),
          ),
          field(
            "Meal was comped",
            checkbox(state.comped, (v) => {
              state.comped = v;
              updateDerivedUI();
            }),
          ),
        ),
        (requestLinkSlotEl = h("div", { class: "request-link-slot" })),
        disclosureSlotEl,
      ),

      section(
        "Photos",
        photoUploader(state, paint, setStatus),
        state.assets.length
          ? h(
              "ul",
              { class: "asset-list" },
              ...state.assets.map((a) =>
                h(
                  "li",
                  { class: "asset" },
                  h("span", { class: "asset-name" }, a.storage_path.split("/").pop() ?? a.storage_path),
                  h(
                    "span",
                    { class: a.is_primary ? "asset-tag is-primary" : "asset-tag" },
                    a.is_primary ? "Primary" : `#${a.sort_order}`,
                  ),
                ),
              ),
            )
          : h("p", { class: "section-hint" }, "No photos uploaded yet."),
      ),

      section(
        "Pre-publish checklist",
        h(
          "ul",
          { class: "checklist" },
          ...PRE_PUBLISH_CHECKLIST.map((item) =>
            h(
              "li",
              { class: "checklist-item" },
              h(
                "label",
                { class: "checklist-label" },
                h("input", {
                  type: "checkbox",
                  checked: state.checklist[item.id] === true,
                  onchange: (e: Event) => {
                    state.checklist[item.id] = (e.target as HTMLInputElement).checked;
                    updateDerivedUI();
                  },
                }),
                h("span", {}, item.label),
                item.required ? h("span", { class: "required-mark", title: "Required" }, " *") : null,
              ),
            ),
          ),
        ),
      ),

      section(
        "Publish",
        guardrailsSlotEl,
        h(
          "div",
          { class: "editor-actions" },
          h(
            "button",
            {
              class: "btn btn-ghost",
              type: "button",
              disabled: !state.id || !state.published,
              onclick: () => publish(state, false, paint, setStatus, opts),
            },
            "Unpublish",
          ),
          publishBtnEl,
        ),
      ),
    );

    updateDerivedUI();
    window.scrollTo(0, scrollY);
  }

  if (opts.reviewId) {
    void loadReview(opts.reviewId, state, paint, setStatus);
  } else if (opts.requestId) {
    void loadReviewRequest(opts.requestId, state, paint, setStatus);
  } else {
    paint();
  }

  return root;
}

interface Guardrails {
  blockers: string[];
}

function renderGuardrailsContent(guardrails: Guardrails): HTMLElement {
  if (guardrails.blockers.length) {
    return h(
      "ul",
      { class: "guardrails" },
      ...guardrails.blockers.map((msg) => h("li", { class: "guardrail" }, msg)),
    );
  }
  return h(
    "p",
    { class: "section-hint section-hint-ok" },
    "All publish guardrails pass.",
  );
}

function checkGuardrails(state: DraftState, final: number | null): Guardrails {
  const blockers: string[] = [];
  if (!state.restaurant_name.trim()) blockers.push("Restaurant name is required.");
  if (!state.address.trim()) blockers.push("Full address is required.");
  if (!state.city.trim()) blockers.push("City is required.");
  if (!state.price_range) blockers.push("Price range is required.");
  if (!state.date_visited) blockers.push("Date visited is required.");
  if (!state.what_ordered.trim()) blockers.push("List of what was ordered is required.");
  if (final === null) blockers.push("All seven scores must be set 1–10 before publishing.");
  if (!state.verdict.trim()) blockers.push("Verdict is required (one sentence).");
  if (!state.return_verdict) blockers.push("Set Would-I-return verdict.");
  for (const cat of CATEGORIES) {
    const col = PARAGRAPH_COLUMNS[cat.key];
    if (col && !state.paragraphs[col].trim()) {
      blockers.push(`Paragraph for ${cat.label} is required.`);
    }
  }
  if (state.contribution_received || state.comped) {
    if (!state.independent_paid_visit_completed) {
      blockers.push(
        "Funded or comped meal: requires at least one independent paid visit before publishing.",
      );
    }
  }
  if (final !== null && final < NEGATIVE_REVIEW_THRESHOLD && state.visit_count < NEGATIVE_REVIEW_MIN_VISITS) {
    blockers.push(
      `Negative review (under ${NEGATIVE_REVIEW_THRESHOLD.toFixed(1)}) requires at least ${NEGATIVE_REVIEW_MIN_VISITS} visits.`,
    );
  }
  for (const item of PRE_PUBLISH_CHECKLIST) {
    if (item.required && state.checklist[item.id] !== true) {
      blockers.push(`Checklist: ${item.label}`);
    }
  }
  return { blockers };
}

async function loadReview(
  id: string,
  state: DraftState,
  paint: () => void,
  setStatus: (m: string, kind?: "info" | "error") => void,
): Promise<void> {
  const { data, error } = await supabase.from("reviews").select("*").eq("id", id).single();
  if (error || !data) {
    setStatus(`Failed to load review: ${error?.message ?? "not found"}`, "error");
    paint();
    return;
  }
  const row = data as ReviewRow;
  state.id = row.id;
  state.restaurant_name = row.restaurant_name;
  state.address = row.address ?? "";
  state.city = row.city ?? "";
  state.neighborhood = row.neighborhood ?? "";
  state.cuisine = row.cuisine ?? "";
  state.price_range = (row.price_range ?? "") as DraftState["price_range"];
  state.date_visited = row.date_visited ?? "";
  state.what_ordered = row.what_ordered ?? "";
  state.visit_count = row.visit_count;
  state.independent_paid_visit_completed = row.independent_paid_visit_completed;
  state.scores = {
    flavor: row.score_flavor,
    seasoning: row.score_seasoning,
    texture: row.score_texture,
    presentation: row.score_presentation,
    value: row.score_value,
    service: row.score_service,
    would_return: row.score_would_return,
  };
  state.paragraphs = {
    paragraph_flavor: row.paragraph_flavor ?? "",
    paragraph_seasoning: row.paragraph_seasoning ?? "",
    paragraph_texture: row.paragraph_texture ?? "",
    paragraph_presentation: row.paragraph_presentation ?? "",
    paragraph_value: row.paragraph_value ?? "",
    paragraph_service: row.paragraph_service ?? "",
  };
  state.verdict = row.verdict ?? "";
  state.return_verdict = (row.return_verdict ?? "") as ReturnVerdict | "";
  state.return_condition = row.return_condition ?? "";
  state.reader_requested = row.reader_requested;
  state.contribution_received = row.contribution_received;
  state.comped = row.comped;
  state.published = row.published;

  const { data: assets } = await supabase
    .from("review_assets")
    .select("*")
    .eq("review_id", id)
    .order("sort_order", { ascending: true });
  state.assets = (assets ?? []) as ReviewAssetRow[];

  paint();
}

async function loadReviewRequest(
  requestId: string,
  state: DraftState,
  paint: () => void,
  setStatus: (m: string, kind?: "info" | "error") => void,
): Promise<void> {
  const { data, error } = await supabase
    .from("review_requests")
    .select("*")
    .eq("id", requestId)
    .single();

  if (error || !data) {
    setStatus(`Failed to load submission: ${error?.message ?? "not found"}`, "error");
    paint();
    return;
  }

  const row = data as ReviewRequestRow;
  state.reviewRequestId = row.id;
  state.restaurant_name = row.restaurant_name;
  state.address = row.address ?? "";
  state.reader_requested = true;
  if (row.contribution_cents > 0 || row.payment_status === "paid") {
    state.contribution_received = true;
  }

  setStatus(`Prefilled from submission by ${row.requester_name}.`);
  paint();
}

async function saveDraft(
  state: DraftState,
  paint: () => void,
  setStatus: (m: string, kind?: "info" | "error") => void,
  opts: EditorOptions,
): Promise<void> {
  setStatus("Saving…");
  const payload = toRow(state, state.published);
  const query = state.id
    ? supabase.from("reviews").update(payload).eq("id", state.id).select().single()
    : supabase.from("reviews").insert(payload).select().single();
  const { data, error } = await query;
  if (error || !data) {
    setStatus(`Save failed: ${error?.message ?? "unknown error"}`, "error");
    return;
  }
  state.id = (data as ReviewRow).id;
  setStatus("Saved.");
  paint();
  opts.onSaved(state.id);
}

async function publish(
  state: DraftState,
  publishedFlag: boolean,
  paint: () => void,
  setStatus: (m: string, kind?: "info" | "error") => void,
  opts: EditorOptions,
): Promise<void> {
  state.published = publishedFlag;
  await saveDraft(state, paint, setStatus, opts);
  if (!state.id) return;

  if (publishedFlag && state.reviewRequestId) {
    const { error: linkError } = await linkContributionsToReview(state.reviewRequestId, state.id);
    if (linkError) {
      setStatus(`Published, but ledger link failed: ${linkError}`, "error");
      return;
    }

    const { error: statusError } = await supabase
      .from("review_requests")
      .update({ status: "reviewed" })
      .eq("id", state.reviewRequestId);

    if (statusError) {
      setStatus(`Published and ledger linked, but submission status update failed: ${statusError.message}`, "error");
      return;
    }
  }

  if (publishedFlag) setStatus("Published.");
  else setStatus("Unpublished.");
}

function toRow(state: DraftState, publishedFlag: boolean): Partial<ReviewRow> {
  const final = weightedFinal(state.scores);
  const slug = state.restaurant_name ? reviewSlug(state.restaurant_name, state.city || null) : null;
  const disclosure =
    state.contribution_received || state.comped ? READER_DISCLOSURE_TEXT : null;
  const row: Partial<ReviewRow> = {
    restaurant_name: state.restaurant_name,
    address: state.address || null,
    city: state.city || null,
    neighborhood: state.neighborhood || null,
    cuisine: state.cuisine || null,
    price_range: state.price_range || null,
    date_visited: state.date_visited || null,
    what_ordered: state.what_ordered || null,
    visit_count: state.visit_count,
    independent_paid_visit_completed: state.independent_paid_visit_completed,
    weights_version: SCORE_WEIGHTS_VERSION,
    final_score: final,
    verdict: state.verdict || null,
    return_verdict: state.return_verdict || null,
    return_condition: state.return_condition || null,
    reader_requested: state.reader_requested,
    contribution_received: state.contribution_received,
    comped: state.comped,
    disclosure_text: disclosure,
    published: publishedFlag,
    slug,
  };
  for (const cat of CATEGORIES) {
    (row as Record<string, unknown>)[`score_${cat.key}` as ScoreColumn] = state.scores[cat.key];
    const pcol = PARAGRAPH_COLUMNS[cat.key];
    if (pcol) (row as Record<string, unknown>)[pcol] = state.paragraphs[pcol] || null;
  }
  return row;
}

/* ---------- UI primitives ---------- */

function section(title: string, ...children: (HTMLElement | null)[]): HTMLElement {
  return h(
    "section",
    { class: "editor-section" },
    h("h2", { class: "editor-section-title" }, title),
    ...children,
  );
}

function field(label: string, control: HTMLElement, hint?: string, required = false): HTMLElement {
  return h(
    "div",
    { class: "field" },
    h(
      "label",
      { class: "label" },
      label,
      required ? h("span", { class: "required-mark", title: "Required", "aria-label": "required" }, " *") : null,
    ),
    control,
    hint ? h("p", { class: "field-hint" }, hint) : null,
  );
}

let comboSeq = 0;

function comboInput(
  value: string,
  options: readonly string[],
  onChange: (v: string) => void,
  placeholder = "",
): HTMLElement {
  const listId = `editor-combo-${++comboSeq}`;
  return h(
    "div",
    { class: "combo-wrap" },
    h("input", {
      type: "text",
      class: "input",
      list: listId,
      value,
      placeholder,
      autocomplete: "off",
      oninput: (e: Event) => onChange((e.target as HTMLInputElement).value),
    }),
    h("datalist", { id: listId }, ...options.map((option) => h("option", { value: option }))),
  );
}

function textInput(value: string, onChange: (v: string) => void, placeholder = ""): HTMLElement {
  const input = h("input", {
    type: "text",
    class: "input",
    value,
    placeholder,
    oninput: (e: Event) => onChange((e.target as HTMLInputElement).value),
  });
  return input;
}

function textarea(value: string, onChange: (v: string) => void, rows = 3, placeholder = ""): HTMLElement {
  return h(
    "textarea",
    {
      class: "input textarea",
      rows: String(rows),
      placeholder,
      oninput: (e: Event) => onChange((e.target as HTMLTextAreaElement).value),
    },
    value,
  );
}

function numberInput(
  value: number,
  min: number,
  max: number,
  step: number,
  onChange: (v: number) => void,
): HTMLElement {
  return h("input", {
    type: "number",
    class: "input",
    value: String(value),
    min: String(min),
    max: String(max),
    step: String(step),
    oninput: (e: Event) => onChange(Number((e.target as HTMLInputElement).value)),
  });
}

function dateInput(value: string, onChange: (v: string) => void): HTMLElement {
  return h("input", {
    type: "date",
    class: "input",
    value,
    oninput: (e: Event) => onChange((e.target as HTMLInputElement).value),
  });
}

function checkbox(value: boolean, onChange: (v: boolean) => void): HTMLElement {
  return h("input", {
    type: "checkbox",
    class: "checkbox",
    checked: value,
    onchange: (e: Event) => onChange((e.target as HTMLInputElement).checked),
  });
}

function priceRangeSelect(
  value: DraftState["price_range"],
  onChange: (v: DraftState["price_range"]) => void,
): HTMLElement {
  return h(
    "select",
    {
      class: "input",
      onchange: (e: Event) => onChange((e.target as HTMLSelectElement).value as DraftState["price_range"]),
    },
    h("option", { value: "" }, "—"),
    h("option", { value: "$", selected: value === "$" }, "$ (under $15)"),
    h("option", { value: "$$", selected: value === "$$" }, "$$ ($15–$30)"),
    h("option", { value: "$$$", selected: value === "$$$" }, "$$$ (over $30)"),
  );
}

function returnSelect(
  value: ReturnVerdict | "",
  onChange: (v: ReturnVerdict | "") => void,
): HTMLElement {
  return h(
    "select",
    {
      class: "input",
      onchange: (e: Event) => onChange((e.target as HTMLSelectElement).value as ReturnVerdict | ""),
    },
    h("option", { value: "" }, "—"),
    h("option", { value: "yes", selected: value === "yes" }, "Yes"),
    h("option", { value: "no", selected: value === "no" }, "No"),
    h("option", { value: "maybe", selected: value === "maybe" }, "Maybe"),
  );
}

function scoreRow(state: DraftState, key: CategoryKey, onScoreChange: () => void): HTMLElement {
  const cat = CATEGORIES.find((c) => c.key === key)!;
  const value = state.scores[key];

  const numberEl = h("input", {
    type: "number",
    min: "1",
    max: "10",
    step: "0.1",
    class: "input score-number",
    value: value === null ? "" : String(value),
    oninput: (e: Event) => {
      const raw = (e.target as HTMLInputElement).value;
      state.scores[key] = raw === "" ? null : clampScore(Number(raw));
      sliderEl.value = state.scores[key] === null ? "5" : String(state.scores[key]);
      onScoreChange();
    },
  }) as HTMLInputElement;

  const sliderEl = h("input", {
    type: "range",
    min: "1",
    max: "10",
    step: "0.5",
    class: "score-slider",
    value: String(value ?? 5),
    oninput: (e: Event) => {
      const next = clampScore(Number((e.target as HTMLInputElement).value));
      state.scores[key] = next;
      numberEl.value = String(next);
      onScoreChange();
    },
  }) as HTMLInputElement;

  return h(
    "div",
    { class: "score-row" },
    h(
      "div",
      { class: "score-row-meta" },
      h(
        "p",
        { class: "score-row-label" },
        cat.label,
        h("span", { class: "required-mark", title: "Required" }, " *"),
      ),
      h("p", { class: "score-row-weight" }, cat.weightLabel),
    ),
    sliderEl,
    numberEl,
  );
}

function photoUploader(
  state: DraftState,
  paint: () => void,
  setStatus: (m: string, kind?: "info" | "error") => void,
): HTMLElement {
  const input = h("input", {
    type: "file",
    accept: "image/*",
    multiple: true,
    class: "input",
    onchange: async (e: Event) => {
      const files = Array.from((e.target as HTMLInputElement).files ?? []);
      if (files.length === 0) return;
      if (!state.id) {
        setStatus("Save the draft once before uploading photos.", "error");
        return;
      }
      setStatus("Uploading…");
      for (const file of files) {
        const ext = fileExtension(file.name);
        const name = photoFilename(
          state.restaurant_name,
          state.city || null,
          null,
          ext,
        );
        const path = `${state.id}/${Date.now()}-${name}`;
        const { error: upErr } = await supabase.storage
          .from(REVIEW_IMAGES_BUCKET)
          .upload(path, file, { contentType: file.type, upsert: false });
        if (upErr) {
          setStatus(`Upload failed: ${upErr.message}`, "error");
          continue;
        }
        const nextOrder = state.assets.length;
        const { data, error } = await supabase
          .from("review_assets")
          .insert({
            review_id: state.id,
            storage_path: path,
            sort_order: nextOrder,
            is_primary: nextOrder === 0,
          })
          .select()
          .single();
        if (error || !data) {
          setStatus(`Photo record failed: ${error?.message ?? "unknown"}`, "error");
          continue;
        }
        state.assets.push(data as ReviewAssetRow);
      }
      setStatus("Photos uploaded.");
      paint();
    },
  });
  return input;
}
